import * as Tooltip from '@radix-ui/react-tooltip'
import { ArrowRightToLine, Check, Copy } from 'lucide-react'
import Prism from 'prismjs'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-javascript'
import { useEffect, useRef, useState } from 'react'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { copyToClipboard } from '@/lib/copy-to-clipboard'
import { cn } from '@/lib/utils'

interface HighlightedCodeBlockProps {
  code: string
  language: 'python' | 'javascript' | 'js'
  className?: string
  /**
   * If provided, renders a "transfer to editor" button immediately to the
   * left of the copy button. Same hover-visibility behavior as copy.
   */
  onTransfer?: () => void
}

/**
 * A code block with syntax highlighting using Prism.js
 * Uses the same prism.css styles as the MDX course content.
 * Includes a copy-to-clipboard button.
 */
export function HighlightedCodeBlock({
  code,
  language,
  className = '',
  onTransfer,
}: HighlightedCodeBlockProps) {
  const codeRef = useRef<HTMLElement>(null)
  const [copied, setCopied] = useState(false)

  // Normalize language name (js -> javascript for Prism)
  const prismLanguage = language === 'js' ? 'javascript' : language

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-highlight when code or language changes
  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current)
    }
  }, [code, prismLanguage])

  const handleCopy = async () => {
    try {
      await copyToClipboard(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  return (
    <div className="group relative">
      <Tooltip.Provider delayDuration={150}>
        <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
          {onTransfer && (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  onClick={onTransfer}
                  className={cn(
                    'rounded-md p-1.5',
                    'bg-muted/80 hover:bg-muted border border-border/50',
                    REVEAL_ON_HOVER,
                  )}
                  aria-label="Transfer code to editor"
                >
                  <ArrowRightToLine className="h-4 w-4" />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                align="center"
                className="border-border bg-background text-foreground rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
              >
                Transfer code to editor
                <Tooltip.Arrow className="fill-background" />
              </Tooltip.Content>
            </Tooltip.Root>
          )}
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                onClick={handleCopy}
                className={cn(
                  'rounded-md p-1.5',
                  'bg-muted/80 hover:bg-muted border border-border/50',
                  REVEAL_ON_HOVER,
                  copied && 'text-emerald-500',
                )}
                aria-label={copied ? 'Copied!' : 'Copy code'}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </Tooltip.Trigger>
            <Tooltip.Content
              side="top"
              align="center"
              className="border-border bg-background text-foreground rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
            >
              {copied ? 'Copied!' : 'Copy code'}
              <Tooltip.Arrow className="fill-background" />
            </Tooltip.Content>
          </Tooltip.Root>
        </div>
      </Tooltip.Provider>
      <pre className={`language-${prismLanguage} ${className}`}>
        <code ref={codeRef} className={`language-${prismLanguage}`}>
          {code}
        </code>
      </pre>
    </div>
  )
}
